import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import axios from 'axios';
import { ArrowLeft, Mail, Lock, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_blueprint-studio-34/artifacts/fe0mwn3b_image.png";

export default function EmailAuthPage() {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ email: '', password: '', name: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setUser, login: googleLogin } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const endpoint = mode === 'login' ? '/auth/login' : '/auth/register';
      const res = await axios.post(`${API}${endpoint}`, form, { withCredentials: true });
      setUser(res.data);
      // Store session token and user for reliable single sign-in
      if (res.data.session_token) {
        sessionStorage.setItem('session_token', res.data.session_token);
      }
      sessionStorage.setItem('user_data', JSON.stringify(res.data));
      navigate('/dashboard', { state: { user: res.data }, replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <a href="/"><img src={LOGO_URL} alt="S.A.I." className="h-16 mx-auto mb-4" /></a>
          <h1 className="text-2xl font-bold tracking-tight text-[#121212]">
            {mode === 'login' ? 'Sign In' : 'Create Account'}
          </h1>
          <p className="text-sm text-[#5C5C5C] mt-1">S.A.I. Design Group</p>
        </div>

        <div className="border border-[#E5E5E5] bg-white p-8">
          {/* Google Login */}
          <Button onClick={googleLogin} variant="outline" data-testid="google-login-btn"
            className="w-full rounded-none h-11 text-sm border-[#E5E5E5] mb-4 hover:bg-[#F5F5F5]">
            <svg width="18" height="18" viewBox="0 0 24 24" className="mr-3">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </Button>

          <div className="flex items-center gap-3 my-5">
            <Separator className="flex-1" />
            <span className="text-xs text-[#5C5C5C] uppercase tracking-wider">or</span>
            <Separator className="flex-1" />
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#5C5C5C] mb-2 block">Full Name</label>
                <div className="relative">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
                  <Input data-testid="auth-name" placeholder="Your full name" value={form.name}
                    onChange={e => setForm({...form, name: e.target.value})}
                    className="rounded-none border-[#E5E5E5] h-11 pl-10" />
                </div>
              </div>
            )}
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#5C5C5C] mb-2 block">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
                <Input data-testid="auth-email" type="email" placeholder="you@company.com" value={form.email}
                  onChange={e => setForm({...form, email: e.target.value})} required
                  className="rounded-none border-[#E5E5E5] h-11 pl-10" />
              </div>
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#5C5C5C] mb-2 block">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
                <Input data-testid="auth-password" type="password" placeholder={mode === 'register' ? 'Min 6 characters' : 'Your password'}
                  value={form.password} onChange={e => setForm({...form, password: e.target.value})} required
                  className="rounded-none border-[#E5E5E5] h-11 pl-10" />
              </div>
            </div>

            {error && <p data-testid="auth-error" className="text-sm text-[#D32F2F] bg-red-50 p-3 border border-red-200">{error}</p>}

            <Button data-testid="auth-submit" type="submit" disabled={loading}
              className="w-full bg-[#121212] text-white hover:bg-[#2A2A2A] rounded-none h-11 text-xs tracking-[0.15em] uppercase">
              {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-5 text-center">
            {mode === 'login' ? (
              <p className="text-sm text-[#5C5C5C]">
                Don't have an account?{' '}
                <button onClick={() => { setMode('register'); setError(''); }} className="text-[#121212] font-bold underline" data-testid="switch-to-register">
                  Sign Up
                </button>
              </p>
            ) : (
              <p className="text-sm text-[#5C5C5C]">
                Already have an account?{' '}
                <button onClick={() => { setMode('login'); setError(''); }} className="text-[#121212] font-bold underline" data-testid="switch-to-login">
                  Sign In
                </button>
              </p>
            )}
          </div>
        </div>

        <div className="text-center mt-6">
          <a href="/" className="text-sm text-[#5C5C5C] hover:text-[#121212] inline-flex items-center gap-2">
            <ArrowLeft size={14} /> Back to Home
          </a>
        </div>
      </div>
    </div>
  );
}
